import { flat } from '../entity/entity';
import type { VaultEntity, Pick } from '../entity/entity';
import { usePlan } from '../plan/usePlan';
import { useCatalog } from '../entity/catalogStore';
import { useWorkspaces } from '../workspaces/useWorkspaces';
import { DocPage } from './DocPage';
import { useTagGround } from '../design/useTagGround';
import { RecordingDoc } from './RecordingDoc';
import { arcsHolding, nooksHolding } from './heldBy';

/**
 * A recording as a document — the sheet it opens into over a nook, as a page:
 * when it was said, the player and its face; the transcript, with Highlights a
 * switch away; and in the margin the arcs it is in and the nooks that hold it.
 */
export function RecordingPage({
  entity,
  onSetFace,
  onSetArchived,
}: {
  entity: VaultEntity;
  onSetFace?: (picks: Pick[]) => void;
  /** Files the recording away, or brings it back; absent where nothing can be written. */
  onSetArchived?: (archived: boolean) => void;
}) {
  const { plan } = usePlan();
  const catalog = useCatalog();
  const nooks = useWorkspaces();
  const arcs = plan.status === 'ready' ? plan.plan.arcs : null;

  const archived = flat(entity.frontMatter['visibility']).trim() === 'archived';
  const tags = catalog.get(entity.path)?.tags ?? [];
  const ground = useTagGround(tags[0]);
  return (
    <DocPage
      ground={ground}
      tags={tags}
      fallback="/thoughts"
      menu={
        onSetArchived
          ? [
              archived
                ? { label: 'Restore', run: () => onSetArchived(false) }
                : { label: 'Archive', run: () => onSetArchived(true) },
            ]
          : []
      }
      margin={[
        { label: 'In', rows: arcsHolding(entity.path, arcs) },
        {
          label: 'Held by',
          rows: nooks.status === 'ready' ? nooksHolding(entity.path, nooks.workspaces, catalog) : [],
        },
      ]}
    >
      <RecordingDoc path={entity.path} raw={entity.raw} onSetFace={onSetFace} />
    </DocPage>
  );
}
